import Analytics from '@/utils/analytics';
import CatalogEmptyState from '@assets/icons/catalog-empty-state.svg';
import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import ScanIcon from '@assets/icons/scan.svg';
import SearchIcon from '@assets/icons/search.svg';
import { Action } from '@/components/navigation-header-content';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AuthStackParams, CatalogStackParams } from '@/navigators/types';
import { CATALOG_ROUTES } from '@/navigators/routes';
import { CatalogCategoryCarousel } from '@/components/catalog-category-carousel';
import { CatalogItemCarousel } from '@/components/catalog-item-carousel';
import { CompositeNavigationProp } from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { FilterOption, SplitFilterList } from '@/components/split-filter-list';
import { FulfillmentAvailability } from '@/components/partners/fulfillment-availability';
import { HorizontalSelector } from '@/components/horizontal-selector';
import {
  ITEM_RECOMMENDATIONS_CAROUSEL_PREFIX,
  ItemRecommendationCarousel,
} from '@/components/orders/item-recommendation-carousel';
import { ItemSearchHeaderContainer } from '@/components/orders/item-search-header-container';
import { KeyboardAwareRecyclerList } from '@/components/recycler-list';
import { NULL_CATEGORY_VALUE } from '@/utils/category';
import { OfflineComponent } from '@/components/offline-component';
import { ScannerOperationsActionSheet } from '@/components/scanner-operations-action-sheet';
import { SplitFilterContainer } from '@/components/split-filter-container';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCatalogCarouselViewability } from '@/hooks/use-catalog-carousel-viewability';
import { useCatalogCategoryItems } from '@/hooks/use-catalog-category-items';
import { useCatalogFilteringEnabled } from '@/hooks/dynamic-config/use-catalog-filtering-enabled';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useHeaderRestaurantSelectorProps } from '@/components/use-header-restaurant-selector-props';
import { useItemRecommendationsContext } from '@/contexts/item-recommendations-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { usePromotionListOptions } from '@/hooks/use-promotion-list-options';
import { useScannerOperations } from '@/hooks/use-scanner-operations';
import { useUserContext } from '@/contexts/user-context';
import { useVariantPackGroupItems } from '@/hooks/use-catalog-variant-pack-group-items';
import { View } from 'react-native';
import { ViewOrderButton } from '@/components/partners/view-order-button';
import { VolumePromotionsCarousel } from '@/components/orders/volume-promotions-carousel';

interface Props {
  navigation: CompositeNavigationProp<
    StackNavigationProp<CatalogStackParams, CATALOG_ROUTES>,
    StackNavigationProp<AuthStackParams>
  >;
}

export const EnhancedCatalogScreen: React.FC<Props> = ({ navigation }) => {
  const baseNavHeaderProps = useHeaderRestaurantSelectorProps();
  const [styles, theme] = useStyles();
  const { itemRecommendations } = useItemRecommendationsContext();
  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();
  const { isGuestMode } = useUserContext();
  const isOfflineModeActive = useOfflineModeActive();
  const { categoryItemsList, hasResponse: categoryItemsHasResponse } =
    useCatalogCategoryItems();
  const { variantPackGroupItemsList, hasResponse: groupItemsHasResponse } =
    useVariantPackGroupItems();
  const promotions = usePromotionListOptions();
  const carouselViewability = useCatalogCarouselViewability();
  const catalogFilteringEnabled = useCatalogFilteringEnabled();
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');
  const scannerOperations = useScannerOperations(CATALOG_ROUTES);

  const [showScanSelectionActionSheet, setShowScanSelectionActionSheet] =
    useState(false);

  const hasResponse = itemGroupsEnabled
    ? groupItemsHasResponse
    : categoryItemsHasResponse;

  const renderSideFilters = !isSmallLayout && !isSmallTabletLayout;

  const filterItems = useMemo<FilterOption[]>(() => {
    if (isGuestMode || !hasResponse) {
      return [];
    }
    const filters: FilterOption[] = [];
    if (promotions.length) {
      filters.push({
        metadata: {
          action: () => {
            navigation.push(CATALOG_ROUTES.VolumePromotionsList);
          },
        },
        title: i18n.t('promotions'),
        value: 'promotions',
      });
    }

    if (itemRecommendations.length) {
      filters.push({
        metadata: {
          action: () => {
            navigation.push(CATALOG_ROUTES.ItemRecommendationsList);
          },
        },
        title: i18n.t('recommendations'),
        value: 'item_recs',
      });
    }

    if (itemGroupsEnabled) {
      for (const { category } of variantPackGroupItemsList) {
        filters.push({
          metadata: {
            action: () => {
              navigation.push(CATALOG_ROUTES.CategoryList, {
                category,
              });
            },
          },
          title: category.name || i18n.t('other'),
          value: category.uuid || NULL_CATEGORY_VALUE,
        });
      }
    } else {
      for (const { category } of categoryItemsList) {
        filters.push({
          metadata: {
            action: () => {
              navigation.push(CATALOG_ROUTES.CategoryList, {
                category,
              });
            },
          },
          title: category.name || i18n.t('other'),
          value: category.name || NULL_CATEGORY_VALUE,
        });
      }
    }

    return filters;
  }, [
    isGuestMode,
    hasResponse,
    promotions.length,
    itemRecommendations.length,
    itemGroupsEnabled,
    navigation,
    variantPackGroupItemsList,
    categoryItemsList,
  ]);

  const filtersVisible = renderSideFilters && filterItems.length > 1;

  const itemSearchHeaderContainerProps = useMemo(() => {
    const onScanPress =
      scannerOperations.length > 0
        ? () => {
            scannerOperations.length > 1
              ? setShowScanSelectionActionSheet(true)
              : navigation.push(CATALOG_ROUTES.Scan);
          }
        : undefined;

    const actions: Action[] = onScanPress
      ? [
          {
            Icon: ScanIcon,
            iconProps: {
              stroke: theme.sk.color.icon.primary,
            },
            onPress: onScanPress,
            testID: 'scan-barcode-icon',
          },
        ]
      : [];

    actions.push({
      Icon: SearchIcon,
      onPress: () => {
        navigation.push(CATALOG_ROUTES.Search, {
          includeGTINFilters: catalogFilteringEnabled && !isGuestMode,
        });
      },
      testID: 'search-icon',
    });

    return {
      navigationHeaderProps: {
        ...baseNavHeaderProps,
        actions: isOfflineModeActive ? [] : actions,
        animate: !filtersVisible,
        backButtonLabel: isSmallLayout ? undefined : i18n.t('back'),
        centerTitle: isGuestMode && isSmallLayout,
        contentFooter: isGuestMode ? null : <FulfillmentAvailability />,
        responsive: true,
        showBackButton: isGuestMode && isSmallLayout,
        subheader:
          !isGuestMode &&
          !renderSideFilters &&
          (categoryItemsList.length || variantPackGroupItemsList.length) ? (
            <View style={styles.categoryBorder}>
              <HorizontalSelector<FilterOption>
                data={filterItems}
                keyExtractor={(item) => String(item.value)}
                onItemPress={(item) => {
                  item?.metadata!.action();
                }}
                selectedItem={null}
                trackingKey="catalog-screen-sections"
              />
            </View>
          ) : null,
        title: i18n.t('catalog'),
      },
      onScanPress,
      statusBarColor: theme.colors.background,
    };
  }, [
    baseNavHeaderProps,
    catalogFilteringEnabled,
    categoryItemsList.length,
    filterItems,
    filtersVisible,
    isGuestMode,
    isOfflineModeActive,
    isSmallLayout,
    navigation,
    renderSideFilters,
    scannerOperations,
    styles.categoryBorder,
    theme,
    variantPackGroupItemsList.length,
  ]);

  const sections = useMemo(
    () => [
      {
        data: itemGroupsEnabled ? variantPackGroupItemsList : categoryItemsList,
        key: null,
      },
    ],
    [categoryItemsList, itemGroupsEnabled, variantPackGroupItemsList],
  );

  return (
    <>
      <ItemSearchHeaderContainer
        {...itemSearchHeaderContainerProps}
        animateNavigationHeader
        includeGTINFilters={catalogFilteringEnabled}
        searchSource="catalog"
      >
        {(scrollViewProps) => (
          <SplitFilterContainer
            FilterComponent={
              <SplitFilterList
                contentContainerStyle={styles.splitFilterContainer}
                data={filterItems}
                scrollEnabled
              />
            }
            filtersVisible={filtersVisible}
          >
            <>
              {!hasResponse && isOfflineModeActive ? (
                <View
                  style={[
                    styles.staticContentContainer,
                    filtersVisible
                      ? null
                      : scrollViewProps.contentContainerStyle,
                  ]}
                >
                  <OfflineComponent />
                </View>
              ) : !hasResponse ? (
                <View
                  style={[
                    styles.staticContentContainer,
                    filtersVisible
                      ? null
                      : scrollViewProps.contentContainerStyle,
                  ]}
                >
                  <ActivityIndicator testID="loading-indicator" />
                </View>
              ) : categoryItemsList.length === 0 &&
                variantPackGroupItemsList.length === 0 ? (
                <EmptyStateMinimal
                  containerStyle={
                    filtersVisible
                      ? null
                      : scrollViewProps.contentContainerStyle
                  }
                  ImageComponent={CatalogEmptyState}
                  text={i18n.t('partner_supplier_catalog_empty_state.message')}
                />
              ) : (
                <KeyboardAwareRecyclerList
                  estimatedItemSize={320}
                  ItemSeparatorComponent={null}
                  ListHeaderComponent={
                    <>
                      {isGuestMode ? null : (
                        <VolumePromotionsCarousel
                          locationPrefix="enhanced_supplier_catalog"
                          onNavigateToDetails={(promotionUUID) => {
                            navigation.navigate(
                              CATALOG_ROUTES.VolumePromotionDetail,
                              { promotionUUID },
                            );
                          }}
                          onSeeAllPress={() => {
                            Analytics.track(
                              'supplier_catalog.promotions_see_all.press',
                            );
                            navigation.navigate(
                              CATALOG_ROUTES.VolumePromotionsList,
                            );
                          }}
                        />
                      )}
                      {itemRecommendations.length > 0 ? (
                        isSmallLayout ? (
                          <ItemRecommendationCarousel
                            items={itemRecommendations.map(
                              (ir) => ir?.metadata!.item,
                            )}
                            onSeeAllPress={() => {
                              navigation.push(
                                CATALOG_ROUTES.ItemRecommendationsList,
                              );
                            }}
                          />
                        ) : (
                          <CatalogItemCarousel
                            count={itemRecommendations.length}
                            listItems={itemRecommendations.map(
                              (ir) => ir?.metadata!.item,
                            )}
                            onSeeAllPress={() => {
                              navigation.push(
                                CATALOG_ROUTES.ItemRecommendationsList,
                              );
                            }}
                            title={i18n.t('based_on_history')}
                            trackingSource={
                              ITEM_RECOMMENDATIONS_CAROUSEL_PREFIX
                            }
                          />
                        )
                      ) : null}
                    </>
                  }
                  renderItem={({ item }) => {
                    return (
                      <CatalogCategoryCarousel
                        category={item.category}
                        categoryItemCount={item.categoryItemCount}
                        listItems={item.items}
                        onCategoryPress={() => {
                          navigation.push(CATALOG_ROUTES.CategoryList, {
                            category: item.category,
                          });
                        }}
                      />
                    );
                  }}
                  sections={sections}
                  {...carouselViewability}
                  {...scrollViewProps}
                  contentContainerStyle={[
                    filtersVisible
                      ? null
                      : scrollViewProps.contentContainerStyle,
                    styles.contentContainerStyle,
                    isSmallLayout ? styles.contentContainerStyleSmall : null,
                  ]}
                />
              )}
              <ViewOrderButton />
            </>
          </SplitFilterContainer>
        )}
      </ItemSearchHeaderContainer>
      {showScanSelectionActionSheet ? (
        <ScannerOperationsActionSheet
          onClose={() => setShowScanSelectionActionSheet(false)}
          operations={scannerOperations}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  categoryBorder: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
  },
  contentContainerStyle: {
    paddingBottom: 8,
  },
  contentContainerStyleSmall: {
    backgroundColor: theme.colors.background,
  },
  splitFilterContainer: {
    marginTop: 8,
  },
  staticContentContainer: {
    flex: 1,
    marginTop: 24,
  },
}));
